import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import DashboardContent from "../components/DashboardContent";
import { CsrAPI } from "../apis/CsrAPI";
import { API_URL } from "../utils/DomainUtils";
import "../scss/chat.scss";

function renderMarkdown(text) {
  const escape = s =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const lines = text.split("\n");
  const out = [];
  let i = 0;

  const inline = s =>
    escape(s)
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/`(.+?)`/g, "<code>$1</code>");

  while (i < lines.length) {
    const line = lines[i];

    if (/^#{1,4}\s/.test(line)) {
      const level = line.match(/^(#{1,4})\s/)[1].length;
      out.push(
        `<h${level}>${inline(line.replace(/^#{1,4}\s+/, ""))}</h${level}>`
      );
      i++;
    } else if (/^---+$/.test(line.trim())) {
      out.push("<hr>");
      i++;
    } else if (/^>\s/.test(line)) {
      const bqLines = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        bqLines.push(inline(lines[i].replace(/^>\s?/, "")));
        i++;
      }
      out.push(`<blockquote><p>${bqLines.join("<br>")}</p></blockquote>`);
    } else if (/^[-*]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*]\s/.test(lines[i])) {
        items.push(`<li>${inline(lines[i].replace(/^[-*]\s+/, ""))}</li>`);
        i++;
      }
      out.push(`<ul>${items.join("")}</ul>`);
    } else if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(`<li>${inline(lines[i].replace(/^\d+\.\s+/, ""))}</li>`);
        i++;
      }
      out.push(`<ol>${items.join("")}</ol>`);
    } else if (line.trim() === "") {
      i++;
    } else {
      const paraLines = [];
      while (
        i < lines.length &&
        lines[i].trim() !== "" &&
        !/^[#\-*>]|^\d+\./.test(lines[i])
      ) {
        paraLines.push(inline(lines[i]));
        i++;
      }
      out.push(`<p>${paraLines.join("<br>")}</p>`);
    }
  }

  return out.join("\n");
}

const TOOL_LABELS = {
  find_study: "Finding study",
  list_studies: "Listing studies",
  get_study_details: "Fetching study details",
  get_request_status: "Checking study status",
  list_sections: "Checking sections",
  explain_section_output: "Explaining section output",
  explain_issues: "Checking issues",
  show_section: "Reading section",
  read_document: "Reading document",
  compare_wizard_answers: "Comparing study answers",
  list_documents: "Listing documents",
  list_protocol_deviations: "Checking protocol deviations",
  assess_deviation: "Assessing deviation",
  record_deviation_decision: "Recording decision",
  get_deviation_decisions: "Loading decisions",
  list_demography_differences: "Checking demographics",
  assess_demography_difference: "Assessing demographic difference",
  record_demography_decision: "Recording decision",
  get_demography_decisions: "Loading decisions",
  review_secondary_efficacy: "Reviewing efficacy data",
};

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays === 0)
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function ChatIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <path d="M21 15c0 .53-.21 1.04-.59 1.41-.37.38-.88.59-1.41.59H7l-4 4V5c0-.53.21-1.04.59-1.41C3.96 3.21 4.47 3 5 3h14c.53 0 1.04.21 1.41.59.38.37.59.88.59 1.41v10z" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

export default function TriloChat() {
  const { getAccessTokenSilently } = useAuth0();

  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [activeToolCalls, setActiveToolCalls] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [studies, setStudies] = useState([]);
  const [selectedStudyId, setSelectedStudyId] = useState("");

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, activeToolCalls, scrollToBottom]);

  // Load sessions and studies on mount
  useEffect(() => {
    CsrAPI.getAllStudies()
      .then(docs => setStudies((docs || []).filter(d => d.id && d.name)))
      .catch(() => {});

    CsrAPI.listChatSessions()
      .then(data => {
        const sorted = (data.sessions || []).sort(
          (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
        );
        setSessions(sorted);
        if (sorted.length > 0) {
          selectSession(sorted[0].session_id);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingSessions(false));
  }, []);

  const selectSession = useCallback(sessionId => {
    setActiveSessionId(sessionId);
    setActiveToolCalls([]);
    CsrAPI.getChatSession({ sessionId }).then(data => {
      setActiveSession(data);
      setMessages(
        (data.messages || []).map(m => ({
          role: m.role,
          content:
            typeof m.content === "string"
              ? m.content
              : JSON.stringify(m.content),
        }))
      );
    });
  }, []);

  const createNewSession = useCallback(async studyId => {
    try {
      const session = await CsrAPI.createChatSession({
        studyId: studyId || null,
      });
      setSessions(prev => [session, ...prev]);
      setActiveSessionId(session.session_id);
      setActiveSession(session);
      setMessages([]);
      setActiveToolCalls([]);
      textareaRef.current?.focus();
    } catch {
      // session creation failed silently; user can retry
    }
  }, []);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || isStreaming) return;

    let sessionId = activeSessionId;

    // Auto-create a session if none is selected
    if (!sessionId) {
      try {
        const session = await CsrAPI.createChatSession({
          studyId: selectedStudyId || null,
        });
        setSessions(prev => [session, ...prev]);
        setActiveSessionId(session.session_id);
        setActiveSession(session);
        sessionId = session.session_id;
      } catch {
        return;
      }
    }

    const userMessage = { role: "user", content: text };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsStreaming(true);
    setActiveToolCalls([]);

    // Auto-resize textarea back down
    if (textareaRef.current) {
      textareaRef.current.style.height = "44px";
    }

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${API_URL}/chat/sessions/${sessionId}/messages`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ message: text }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();

        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(part.slice(6));
            if (event.type === "tool_call") {
              setActiveToolCalls(prev => [...prev, event.name]);
            } else if (event.type === "text") {
              setMessages(prev => [
                ...prev,
                { role: "assistant", content: event.content },
              ]);
              setActiveToolCalls([]);
              // Update session title in the list after first reply
              setSessions(prev =>
                prev.map(s =>
                  s.session_id === sessionId
                    ? {
                        ...s,
                        title: s.title || text.slice(0, 60),
                        updated_at: new Date().toISOString(),
                      }
                    : s
                )
              );
            } else if (event.type === "error") {
              setMessages(prev => [
                ...prev,
                { role: "error", content: event.message },
              ]);
              setActiveToolCalls([]);
            }
          } catch {
            // malformed SSE line — skip
          }
        }
      }
    } catch {
      setMessages(prev => [
        ...prev,
        { role: "error", content: "Something went wrong. Please try again." },
      ]);
    } finally {
      setIsStreaming(false);
      setActiveToolCalls([]);
    }
  }, [
    input,
    isStreaming,
    activeSessionId,
    selectedStudyId,
    getAccessTokenSilently,
  ]);

  const handleKeyDown = useCallback(
    e => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );

  const handleTextareaChange = useCallback(e => {
    setInput(e.target.value);
    // Auto-grow textarea
    const el = e.target;
    el.style.height = "44px";
    el.style.height = Math.min(el.scrollHeight, 160) + "px";
  }, []);

  const pageContent = (
    <div className="trilochat">
      {/* ── Sessions sidebar ── */}
      <div className="trilochat__sessions">
        <div className="trilochat__sessions-header">
          <h3>TriloChat</h3>
          <button
            className="trilochat__new-btn"
            onClick={() => createNewSession(selectedStudyId)}>
            <PlusIcon />
            New chat
          </button>
        </div>
        <div className="trilochat__sessions-list">
          {loadingSessions ? null : sessions.length === 0 ? (
            <p className="trilochat__sessions-empty">No conversations yet</p>
          ) : (
            sessions.map(s => (
              <button
                key={s.session_id}
                className={`trilochat__session-item ${s.session_id === activeSessionId ? "trilochat__session-item--active" : ""}`}
                onClick={() => selectSession(s.session_id)}>
                <div>
                  <div className="trilochat__session-title">
                    {s.title || "New chat"}
                  </div>
                  <div className="trilochat__session-time">
                    {formatTime(s.updated_at)}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* ── Main chat area ── */}
      <div className="trilochat__main">
        {!activeSessionId ? (
          <div className="trilochat__no-session">
            <ChatIcon />
            <h3>Ask TriloChat anything</h3>
            <p>
              Ask about your study data, section outputs, protocol deviations,
              demographics, or efficacy results. TriloChat uses your real study
              data to answer.
            </p>
            {studies.length > 0 && (
              <div className="trilochat__study-selector">
                <label className="trilochat__study-label">Study context</label>
                <select
                  className="trilochat__study-select"
                  value={selectedStudyId}
                  onChange={e => setSelectedStudyId(e.target.value)}>
                  <option value="">No study context</option>
                  {studies.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <button onClick={() => createNewSession(selectedStudyId)}>
              Start a conversation
            </button>
          </div>
        ) : (
          <>
            <div className="trilochat__chat-header">
              <h2>{activeSession?.title || "New chat"}</h2>
              {studies.length > 0 && (
                <div className="trilochat__study-selector">
                  <label className="trilochat__study-label">Study</label>
                  <select
                    className="trilochat__study-select"
                    value={selectedStudyId}
                    onChange={e => setSelectedStudyId(e.target.value)}>
                    <option value="">No study context</option>
                    {studies.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="trilochat__messages">
              {messages.length === 0 && !isStreaming && (
                <div className="trilochat__empty-state">
                  <ChatIcon />
                  <h3>Ready to help</h3>
                  <p>
                    Try asking &ldquo;list my studies&rdquo;, &ldquo;show me
                    demographics for [study name]&rdquo;, or &ldquo;explain why
                    section X is failing&rdquo;.
                  </p>
                </div>
              )}

              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`trilochat__message trilochat__message--${msg.role}`}>
                  <div
                    className={`trilochat__avatar trilochat__avatar--${msg.role === "user" ? "user" : "assistant"}`}>
                    {msg.role === "user" ? "You" : "TC"}
                  </div>
                  <div className="trilochat__bubble">
                    {msg.role === "assistant" ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: renderMarkdown(msg.content),
                        }}
                      />
                    ) : (
                      msg.content
                    )}
                  </div>
                </div>
              ))}

              {activeToolCalls.length > 0 && (
                <div className="trilochat__thinking">
                  {activeToolCalls.map((name, i) => (
                    <span key={i} className="trilochat__tool-pill">
                      <span className="trilochat__tool-dot" />
                      {TOOL_LABELS[name] || name}…
                    </span>
                  ))}
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <div className="trilochat__input-area">
              <div className="trilochat__input-row">
                <textarea
                  ref={textareaRef}
                  className="trilochat__textarea"
                  rows={1}
                  placeholder="Ask anything about your studies…"
                  value={input}
                  onChange={handleTextareaChange}
                  onKeyDown={handleKeyDown}
                  disabled={isStreaming}
                />
                <button
                  className="trilochat__send-btn"
                  onClick={sendMessage}
                  disabled={!input.trim() || isStreaming}
                  aria-label="Send message">
                  <SendIcon />
                </button>
              </div>
              <p className="trilochat__hint">
                Enter to send · Shift+Enter for new line
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <DashboardContent
      sidebarCSR={false}
      headerTitle=""
      headerButton={false}
      pageContent={pageContent}
    />
  );
}
